"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { cx } from "@frontend/components/ui";

const LENGTH = 6;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "").slice(0, LENGTH);
}

/**
 * Six single-digit boxes for a WhatsApp OTP. Keeps a hidden `name="code"` field
 * for form posts, and accepts paste / SMS autofill of the full code.
 */
export function OtpInput({
  name = "code",
  autoFocus = true,
  disabled = false,
}: {
  name?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const labelId = useId();
  const [digits, setDigits] = useState<string[]>(() => Array.from({ length: LENGTH }, () => ""));
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const code = digits.join("");

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function focusAt(index: number) {
    const el = refs.current[Math.max(0, Math.min(LENGTH - 1, index))];
    el?.focus();
    el?.select();
  }

  function applyCode(raw: string, startIndex = 0) {
    const next = digitsOnly(raw);
    if (!next) return;

    setDigits((current) => {
      const copy = [...current];
      for (let i = 0; i < next.length && startIndex + i < LENGTH; i += 1) {
        copy[startIndex + i] = next[i]!;
      }
      return copy;
    });

    const advanceTo = Math.min(startIndex + next.length, LENGTH - 1);
    // Defer so the new value is painted before focus moves.
    requestAnimationFrame(() => focusAt(advanceTo));
  }

  function onChangeAt(index: number, value: string) {
    const cleaned = digitsOnly(value);
    if (cleaned.length > 1) {
      applyCode(cleaned, index);
      return;
    }

    setDigits((current) => {
      const copy = [...current];
      copy[index] = cleaned;
      return copy;
    });

    if (cleaned && index < LENGTH - 1) {
      requestAnimationFrame(() => focusAt(index + 1));
    }
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      setDigits((current) => {
        const copy = [...current];
        copy[index - 1] = "";
        return copy;
      });
      focusAt(index - 1);
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusAt(index - 1);
    }
    if (event.key === "ArrowRight" && index < LENGTH - 1) {
      event.preventDefault();
      focusAt(index + 1);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    if (!/\d/.test(pasted)) return;
    event.preventDefault();
    applyCode(pasted, 0);
  }

  return (
    <div>
      <p id={labelId} className="field-label">
        Code from WhatsApp
      </p>
      <input type="hidden" name={name} value={code} />
      <div
        role="group"
        aria-labelledby={labelId}
        className="mt-1.5 grid grid-cols-6 gap-2 sm:gap-2.5"
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={index === 0 ? LENGTH : 1}
            value={digit}
            disabled={disabled}
            aria-label={`Digit ${index + 1} of ${LENGTH}`}
            onChange={(event) => onChangeAt(index, event.target.value)}
            onKeyDown={(event) => onKeyDown(index, event)}
            onPaste={onPaste}
            onFocus={(event) => event.currentTarget.select()}
            className={cx(
              "aspect-square w-full rounded-xl border bg-surface text-center font-mono text-lg font-semibold tabular-nums text-ink outline-none transition sm:text-xl",
              digit
                ? "border-brand-500 shadow-[0_0_0_1px] shadow-brand-500/30"
                : "border-line",
              "focus:border-brand-500 focus:ring-4 focus:ring-brand-100",
              "disabled:opacity-50",
            )}
          />
        ))}
      </div>
    </div>
  );
}
